import React from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../../components/AppActionMenu'

export default function FeedbackTable({
  records = [],
  loading = false,
}) {
  const navigate = useNavigate()

  const getTypeBadge = (type = '') => {
    const lower = type.toLowerCase()
    if (lower === 'problem') {
      return 'bg-rose-500/10 text-rose-800 border-rose-500/30'
    }
    if (lower === 'suggestion') {
      return 'bg-emerald-500/10 text-emerald-800 border-emerald-500/30'
    }
    return 'bg-blue-500/10 text-blue-800 border-blue-500/30'
  }

  const formatDate = (dStr) => {
    if (!dStr) return '—'
    try {
      return new Date(dStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dStr
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1020px] text-left text-xs text-on-surface">
          {/* ── Table Header ── */}
          <thead className="border-b border-outline-variant/25 bg-surface-container-low/80 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant select-none">
            <tr>
              <th className="w-48 px-4 py-3.5">
                <span>Name</span>
              </th>
              <th className="w-36 px-4 py-3.5">
                <span>Date &amp; Type</span>
              </th>
              <th className="px-4 py-3.5">
                <span>Feedback / Query</span>
              </th>
              <th className="px-4 py-3.5">
                <span>Reply</span>
              </th>
              <th className="w-44 px-4 py-3.5">
                <span>Reply By / On</span>
              </th>
              <th className="w-16 px-4 py-3.5 text-center">
                <span>Action</span>
              </th>
            </tr>
          </thead>

          {/* ── Table Body ── */}
          <tbody className="divide-y divide-outline-variant/15 font-medium">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                      progress_activity
                    </span>
                    <span>Loading student feedback &amp; queries...</span>
                  </div>
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline-variant">
                      chat_bubble_outline
                    </span>
                    <p className="font-bold text-on-surface">No Feedback or Queries Found</p>
                    <p className="text-[11px]">No student submissions matching selected filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              records.map((item) => (
                <tr
                  key={item.id}
                  className="group transition-colors hover:bg-surface-container-low/50"
                >

                  {/* Student Name */}
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/cloud/feedback/reply/${item.id}`)}
                        className="font-display text-sm font-extrabold text-[#3b82f6] hover:underline text-left cursor-pointer"
                      >
                        {item.name}
                      </button>
                      <span className="text-[11px] text-on-surface-variant">
                        {item.courseName} • {item.branchName}
                      </span>
                    </div>
                  </td>

                  {/* Date & Type */}
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-col gap-1.5 items-start">
                      <span className="text-xs font-bold text-on-surface flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs text-primary">calendar_today</span>
                        <span>{formatDate(item.date)}</span>
                      </span>
                      <span
                        className={`inline-flex items-center rounded-xl border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${getTypeBadge(
                          item.type
                        )}`}
                      >
                        {item.type}
                      </span>
                    </div>
                  </td>

                  {/* Feedback Text */}
                  <td className="px-4 py-4 align-top">
                    <p className="text-xs text-on-surface font-medium leading-relaxed">
                      {item.feedback}
                    </p>
                  </td>

                  {/* Reply */}
                  <td className="px-4 py-4 align-top">
                    {item.reply ? (
                      <p className="text-xs text-on-surface-variant font-medium leading-relaxed">
                        {item.reply}
                      </p>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-amber-500/10 px-2 py-0.5 text-[11px] font-bold text-amber-800 border border-amber-500/20">
                        <span className="material-symbols-outlined text-xs">pending</span>
                        <span>Pending Response</span>
                      </span>
                    )}
                  </td>

                  {/* Reply By & On */}
                  <td className="px-4 py-4 align-top">
                    {item.replyBy ? (
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="font-bold text-on-surface flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs text-primary">person</span>
                          <span>{item.replyBy}</span>
                        </span>
                        <span className="text-[11px] text-on-surface-variant">
                          {item.replyOn}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-on-surface-variant">—</span>
                    )}
                  </td>

                  {/* Action Menu */}
                  <td className="px-4 py-4 align-top text-center">
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'reply', label: item.reply ? 'Edit Response' : 'Reply to Query', icon: 'reply' },
                      ]}
                      onSelect={(actionKey) => {
                        if (actionKey === 'reply') navigate(`/cloud/feedback/reply/${item.id}`)
                      }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
